/*
 * Desktop nav panels. The trigger is a link first and a disclosure second, so
 * a click navigates on a mouse, while a first tap on a touch screen opens the
 * panel instead. Panels close on mouse leave, Escape, scroll and outside click.
 */
(function () {
  const OPEN_DELAY = 120;
  const SWAP_DELAY = 60;
  const CLOSE_DELAY = 180;
  // Resting on a plain link such as Nutrition closes the panel, but only after
  // long enough for a pointer on its way to the next panel item to cross it.
  const PLAIN_GRACE = 500;
  // A panel reopening this soon after a close appears at once, like a swap.
  const REOPEN_WINDOW = 300;

  const nav = document.querySelector('.capra-nav');
  if (!nav) return;

  const items = Array.from(nav.querySelectorAll('.capra-nav__item--has-panel'));
  if (!items.length) return;

  let openItem = null;
  let openTimer = null;
  let closeTimer = null;
  let closedAt = -Infinity;

  const panelOf = (item) => item.querySelector('[data-capra-nav-panel]');
  const triggerOf = (item) => item.querySelector('[data-capra-nav-trigger]');
  const canHover = () => window.matchMedia('(hover: hover)').matches;

  const header = nav.closest('.header-wrapper') || nav.closest('.header');
  const firstItem = nav.querySelector('.capra-nav__item');
  let scrim = null;

  // The nav is centred in the header grid, so the inset that lines a panel's
  // first column up with the first nav item can only be measured, not written.
  function align(panel) {
    if (!firstItem) return;
    const left = Math.round(firstItem.getBoundingClientRect().left);
    panel.style.setProperty('--capra-nav-inset', `${left}px`);
  }

  function showScrim() {
    if (!scrim) {
      scrim = document.createElement('div');
      scrim.className = 'capra-nav__scrim';
      document.body.appendChild(scrim);
    }
    scrim.style.top = `${Math.round(header.getBoundingClientRect().bottom)}px`;
    requestAnimationFrame(() => scrim.classList.add('is-open'));
  }

  function hideScrim() {
    if (scrim) scrim.classList.remove('is-open');
  }

  function clearTimers() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
  }

  function open(item) {
    if (openItem === item) return;

    const panel = panelOf(item);
    const trigger = triggerOf(item);
    if (!panel || !trigger) return;

    // Swapping from one panel to another has to be instant: fading the new one
    // in from nothing lets the page show through for a few frames.
    const swapping = Boolean(openItem);
    if (swapping) close(openItem, true);
    const instant = swapping || performance.now() - closedAt < REOPEN_WINDOW;

    panel.hidden = false;
    align(panel);
    if (!swapping) showScrim();

    if (instant) {
      panel.classList.add('is-instant', 'is-open');
      requestAnimationFrame(() => panel.classList.remove('is-instant'));
    } else {
      requestAnimationFrame(() => panel.classList.add('is-open'));
    }
    trigger.setAttribute('aria-expanded', 'true');
    item.classList.add('is-open');
    openItem = item;
  }

  function close(item, keepScrim) {
    const panel = panelOf(item);
    const trigger = triggerOf(item);
    if (!panel || !trigger) return;

    panel.classList.remove('is-open');
    panel.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    item.classList.remove('is-open');
    if (openItem === item) {
      openItem = null;
      if (!keepScrim) {
        hideScrim();
        closedAt = performance.now();
      }
    }
  }

  function closeAll() {
    clearTimers();
    if (openItem) close(openItem);
  }

  items.forEach((item) => {
    const trigger = triggerOf(item);

    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch' || !canHover()) return;
      clearTimers();
      openTimer = setTimeout(() => open(item), openItem ? SWAP_DELAY : OPEN_DELAY);
    });

    // A first tap on a touch screen opens the panel rather than navigating.
    // The press itself focuses the link and the focus handler opens the panel,
    // so the decision has to be made from the state before the press, not from
    // what is open by the time the click arrives.
    let openBeforePress = false;

    item.addEventListener('pointerdown', () => {
      openBeforePress = openItem === item;
    });

    trigger.addEventListener('click', (event) => {
      if (canHover()) return;
      if (event.detail === 0) return; // Enter on the keyboard, not a tap
      if (openBeforePress) return;
      event.preventDefault();
      clearTimers();
      open(item);
    });

    item.addEventListener('focusin', () => {
      clearTimers();
      open(item);
    });

    item.addEventListener('focusout', () => {
      setTimeout(() => {
        if (!item.contains(document.activeElement)) close(item);
      });
    });
  });

  nav.querySelectorAll('.capra-nav__item:not(.capra-nav__item--has-panel)').forEach((item) => {
    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch' || !canHover() || !openItem) return;
      clearTimers();
      closeTimer = setTimeout(closeAll, PLAIN_GRACE);
    });
  });

  // Leaving is judged on the whole nav, panels included, so the gaps between
  // items and the plain links between panel items do not count as leaving.
  nav.addEventListener('pointerleave', (event) => {
    if (event.pointerType === 'touch' || !canHover()) return;
    clearTimers();
    closeTimer = setTimeout(closeAll, CLOSE_DELAY);
  });

  // On the document, not the nav: a panel opened by hover leaves focus on the
  // body, where a nav-scoped listener would never see the key.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openItem) return;
    const trigger = triggerOf(openItem);
    const hadFocus = openItem.contains(document.activeElement);
    close(openItem);
    if (hadFocus && trigger) trigger.focus();
  });

  window.addEventListener('scroll', closeAll, { passive: true });
  window.addEventListener('resize', closeAll);

  document.addEventListener('click', (event) => {
    if (openItem && !openItem.contains(event.target)) closeAll();
  });

  // The cart popover anchors under the header, so a panel left open would sit
  // over it. Dawn toggles `active` on the drawer for both the pill click and
  // the add-to-cart flow.
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    new MutationObserver(() => {
      if (cartDrawer.classList.contains('active')) closeAll();
    }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }
})();
